import React from 'react';
import { Link } from 'react-router-dom';

const Terms: React.FC = () => {
  return (
    <div className="pt-48 pb-40 md:pt-48 md:pb-48">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-4">
            <span className="text-white">Terms of</span>
            <span className="block mt-4 bg-gradient-to-r from-primary to-[#E6C45C] text-transparent bg-clip-text">Service</span>
          </h1>
          <p className="text-[#FFFFFFCC] mt-6">
            Last updated: {new Date().toLocaleDateString('en-IE', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>

        {/* Content */}
        <div className="prose prose-lg prose-invert max-w-none">
          {/* Introduction */}
          <section className="mb-12">
            <p className="text-[#FFFFFFCC] leading-relaxed">
              Please read these Terms of Service ("Terms", "Terms of Service") carefully before using the Digital Memorials website (digitalmemorials.ie) operated by Digital Memorials ("us", "we", "our", or "Company"). Your access to and use of the Service is conditioned on your acceptance of and compliance with these Terms. These Terms apply to all visitors, users, and others who access or use the Service.
            </p>
            <p className="text-[#FFFFFFCC] leading-relaxed mt-4">
              By accessing or using the Service, you agree to be bound by these Terms. If you disagree with any part of the terms, then you may not access the Service.
            </p>
          </section>

          {/* Definitions */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">Definitions</h2>
            <ul className="list-disc pl-6 space-y-2 text-[#FFFFFFCC]">
              <li>"Service" refers to the Digital Memorials website and memorial creation services.</li>
              <li>"Memorial" refers to the Digital Memorial apps created through our Service.</li>
              <li>"Content" refers to all text, images, videos, and other materials uploaded to or displayed on the Memorial.</li>
              <li>"User" refers to any individual or entity that accesses or uses the Service.</li>
              <li>"Subscriber" refers to Users who have registered and created a memorial through our Service.</li>
            </ul>
          </section>

          {/* Account Terms */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">Account Terms</h2>
            <p className="text-[#FFFFFFCC] leading-relaxed mb-4">
              When you create an account with us, you must provide accurate, complete, and current information. Failure to do so constitutes a breach of the Terms, which may result in immediate termination of your account on our Service.
            </p>
            <ul className="list-disc pl-6 space-y-2 text-[#FFFFFFCC]">
              <li>You are responsible for safeguarding the password and access to your account.</li>
              <li>You agree not to disclose your password to any third party.</li>
              <li>You must notify us immediately upon becoming aware of any breach of security or unauthorized use of your account.</li>
              <li>You may not use as a username the name of another person or entity that is not lawfully available for use.</li>
            </ul>
          </section>

          {/* Memorial Content */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">Memorial Content</h2>
            <p className="text-[#FFFFFFCC] leading-relaxed mb-4">
              Our Service allows you to create and maintain Digital Memorials. You are responsible for all Content that you upload, post, or otherwise make available via your memorial:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-[#FFFFFFCC]">
              <li>You must have the legal right to post any Content on your memorial.</li>
              <li>Content must not infringe on any intellectual property rights.</li>
              <li>Content must not be defamatory, offensive, or violate any applicable laws.</li>
              <li>You grant us a license to use, store, and display the Content for the purpose of providing the Service.</li>
              <li>We reserve the right to remove any Content that violates these Terms.</li>
            </ul>
          </section>

          {/* Intellectual Property */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">Intellectual Property Rights</h2>
            <p className="text-[#FFFFFFCC] leading-relaxed">
              The Service and its original content (excluding Content provided by users), features, and functionality are and will remain the exclusive property of Digital Memorials and its licensors. The Service is protected by copyright, trademark, and other laws of Ireland and foreign countries. Our trademarks and trade dress may not be used in connection with any product or service without the prior written consent of Digital Memorials.
            </p>
          </section>

          {/* Payment Terms */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">Payment Terms</h2>
            <p className="text-[#FFFFFFCC] leading-relaxed mb-4">
              Certain aspects of the Service may be provided for a fee. You agree to pay all fees in accordance with the pricing and payment terms presented to you for the Service.
            </p>
            <ul className="list-disc pl-6 space-y-2 text-[#FFFFFFCC]">
              <li>Fees are non-refundable except as required by law or as explicitly stated in our refund policy.</li>
              <li>You are responsible for all applicable taxes and fees.</li>
              <li>Recurring charges will be billed in advance on a regular basis (monthly/annually).</li>
              <li>You agree to provide current, complete, and accurate purchase and account information.</li>
            </ul>
          </section>

          {/* Termination */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">Termination</h2>
            <p className="text-[#FFFFFFCC] leading-relaxed">
              We may terminate or suspend access to our Service immediately, without prior notice or liability, for any reason whatsoever, including without limitation if you breach the Terms. Upon termination, your right to use the Service will immediately cease. All provisions of the Terms which by their nature should survive termination shall survive termination, including, without limitation, ownership provisions, warranty disclaimers, indemnity, and limitations of liability.
            </p>
          </section>

          {/* Limitation of Liability */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">Limitation of Liability</h2>
            <p className="text-[#FFFFFFCC] leading-relaxed">
              In no event shall Digital Memorials, nor its directors, employees, partners, agents, suppliers, or affiliates, be liable for any indirect, incidental, special, consequential or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from your access to or use of or inability to access or use the Service.
            </p>
          </section>

          {/* Disclaimer */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">Disclaimer</h2>
            <p className="text-[#FFFFFFCC] leading-relaxed">
              Your use of the Service is at your sole risk. The Service is provided on an "AS IS" and "AS AVAILABLE" basis. The Service is provided without warranties of any kind, whether express or implied, including, but not limited to, implied warranties of merchantability, fitness for a particular purpose, non-infringement or course of performance.
            </p>
          </section>

          {/* Governing Law */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">Governing Law</h2>
            <p className="text-[#FFFFFFCC] leading-relaxed">
              These Terms shall be governed and construed in accordance with the laws of Ireland, without regard to its conflict of law provisions. Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights. If any provision of these Terms is held to be invalid or unenforceable by a court, the remaining provisions of these Terms will remain in effect.
            </p>
          </section>

          {/* Changes to Terms */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">Changes to Terms</h2>
            <p className="text-[#FFFFFFCC] leading-relaxed">
              We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material, we will try to provide at least 30 days' notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion. By continuing to access or use our Service after those revisions become effective, you agree to be bound by the revised terms.
            </p>
          </section>

          {/* Contact Information */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">Contact Us</h2>
            <p className="text-[#FFFFFFCC] leading-relaxed">
              If you have any questions about these Terms, please contact us:
            </p>
            <div className="mt-4 text-[#FFFFFFCC]">
              <p>Digital Memorials</p>
              <p>Email: mail@digitalmemorials.ie</p>
              <p className="mt-4">
                For privacy-related matters, please refer to our{' '}
                <Link 
                  to="/privacy" 
                  onClick={() => window.scrollTo(0, 0)}
                  className="text-primary hover:text-[#E6C45C] transition-colors duration-200"
                >
                  Privacy Policy
                </Link>
                .
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Terms; 